import { useNavigate, useParams } from 'react-router-dom'

import { Box, BoxList, Form } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import {
  AtsJobApplicationOfferLetterPageDocument,
  JobApplicationOfferLetterApplicationFragment,
  JobApplicationOfferLetterFragment,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import Navigator from 'modules/core/components/Navigator'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { OfferLetterStatusTag } from 'modules/ats/components/OfferLetterStatusTag'
import EsignatureTitle from 'modules/documents/components/DocumentPreview/Content/Esignature/Title'
import StatusItem from 'modules/documents/components/DocumentPreview/Content/StatusItem'
import type { StatusStruct } from 'modules/documents/components/DocumentPreview/Content/StatusItem'
import FormLayout from 'modules/documents/components/DocumentPreview/FormLayout'
import DocumentPreviewLayout from 'modules/documents/components/DocumentPreview/Layout'

const toStatusStruct = (
  offerLetter: JobApplicationOfferLetterFragment,
  application: JobApplicationOfferLetterApplicationFragment
): StatusStruct => ({
  signerFullName: application.candidate.fullName,
  signerAvatar: application.photo ?? undefined,
  description: offerLetter.declinedReason ?? undefined,
  statusText: getStatusText(offerLetter.status),
  statusDate: offerLetter.createdAt,
})

/* TO DO: return OfferLetterStatusesEnum as the status
 * in the Entity to get the type from GraphQl
 * Then use it as the status type.
 */

const getStatusText = (status: string): string => {
  return (
    {
      created: i18n.t('ats.offer_letter.status.created'),
      pending_signature: i18n.t('ats.offer_letter.status.pending'),
      accepted: i18n.t('ats.offer_letter.status.signed'),
      canceled: i18n.t('ats.offer_letter.status.canceled'),
      declined: i18n.t('ats.offer_letter.status.declined'),
    }[status] || i18n.t('ats.offer_letter.status.created')
  )
}

const JobApplicationOfferLetterPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const params = useParams()
  const jobPostingId = String(params.jobId)
  const applicationId = String(params.application_id)

  const { data } = useQuery(AtsJobApplicationOfferLetterPageDocument, {
    variables: { applicationId, jobPostingId },
  })

  const offerLetter = data?.ats.offerLettersConnection.nodes[0]
  const application = data?.ats.application

  if (!application || !offerLetter?.document) return null
  const { url, filename, contentType: mimeType } = offerLetter.document

  const onClose = () => {
    navigate(
      navigation.ats.jobs.jobsId.applications.applicationDetail.files.fullPath({
        jobId: jobPostingId,
        application_id: applicationId,
      })
    )
  }

  return (
    <DocumentPreviewLayout
      onClose={onClose}
      fileViewerProps={{ filename, mimeType, url, isUrlAbsolute: true }}
      content={
        <>
          <Navigator title={filename} onCancel={onClose} />

          <Box paddingY="s32" paddingX="s24" gap="s40">
            <FormLayout>
              <Form padding={null}>
                <TextInput
                  label={i18n.t('documents.modal.file_name')}
                  field={new Field(filename, 'string')}
                  readonly
                />
                <Select
                  label={i18n.t('ats.candidates.files.offer_letter.file_type')}
                  field={new Field('offer_letter', 'string')}
                  options={[
                    {
                      value: 'offer_letter',
                      label: i18n.t('ats.candidates.files.offer_letter.header'),
                    },
                  ]}
                  readonly
                  none={i18n.t('common.none')}
                />
              </Form>
            </FormLayout>

            <Box gap="s16">
              <EsignatureTitle
                description={i18n.t('documents.modal.esignature.description_required')}
              />

              <BoxList>
                <StatusItem
                  status={toStatusStruct(offerLetter, application)}
                  statusBox={<OfferLetterStatusTag status={offerLetter.status} />}
                />
              </BoxList>
            </Box>
          </Box>
        </>
      }
    />
  )
}

export default JobApplicationOfferLetterPage
